import { match } from 'ts-pattern';

import {
  FiatACHPaymentForm,
  FiatCOPBeneficiaryCompanyPaymentForm,
  FiatCOPPaymentForm,
  FiatCOPThirdPartyCompanyPaymentForm,
  FiatCvuPaymentForm,
  FiatPaymentForm,
  FiatPixPaymentForm,
  FiatSpeiPaymentForm,
  FiatSwiftPaymentForm,
  FiatWirePaymentForm,
  isFiatACHPaymentForm,
  isFiatCOPBeneficiaryPaymentForm,
  isFiatCOPPaymentForm,
  isFiatCOPThirdPartyPaymentForm,
  isFiatCvuPaymentForm,
  isFiatPixPaymentForm,
  isFiatSpeiPaymentForm,
  isFiatSwiftPaymentForm,
  isFiatWirePaymentForm,
} from '@/schemas/paymentMethod';
import { BankAccountType } from '@/types/bankAccountType';
import { Contact, ContactType, SelfContact } from '@/types/contacts';
import { isCompanyContact, isIndividualContact } from '@/types/contacts.ext';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { Customer } from '@/types/customer';
import { CreateRecipientRequest } from '@/types/recipient';
import { isProbablyValidIBAN } from '@/utility/iban';

const getIndividualName = (contact: Contact | SelfContact): string | undefined => {
  const { entity } = contact;
  if (isIndividualContact(entity)) {
    return `${entity.firstName} ${entity.lastName}`;
  }
  return undefined;
};

const getBusinessName = (contact: Contact | SelfContact): string | undefined => {
  const { entity } = contact;
  if (isCompanyContact(entity)) {
    return entity.legalEntityName;
  }
  return undefined;
};

const getContactType = (contact: Contact | SelfContact): ContactType => {
  return match(contact.entity)
    .when(isIndividualContact, (): ContactType => ContactType.individual)
    .when(isCompanyContact, (): ContactType => ContactType.company)
    .exhaustive();
};

const getCountryOfIncorporation = (
  contact: Contact | SelfContact,
  customer?: Customer,
): string | undefined => {
  if (isCompanyContact(contact.entity)) {
    if ('country' in contact) {
      return (contact as Contact).country;
    }
    return customer?.businessInfo?.country ?? customer?.businessInfo?.address?.country ?? undefined;
  }
  return undefined;
};

const getRecipientAddress = (
  contact: Contact | SelfContact,
  formCountry: string,
  customer?: Customer,
): Record<string, unknown> => {
  if (contact.id !== null) {
    const c = contact as Contact;
    return {
      street1: c.address.street,
      street2: c.address.complement,
      city: c.address.city,
      state: c.address.state,
      postalCode: c.address.zipCode,
      country: c.country,
    };
  }
  // SelfContact: use the beneficiary's own address
  if (customer?.business?.address) {
    const addr = customer.business.address;
    return {
      street1: addr.streetOne || addr.address,
      street2: addr.streetTwo || null,
      city: addr.city,
      state: addr.state,
      postalCode: addr.zipcode,
      country: formCountry,
    };
  }
  if (customer?.businessInfo?.address) {
    const addr = customer.businessInfo.address;
    return {
      street1: addr.streetAddress || null,
      street2: addr.complement || null,
      city: addr.city,
      state: addr.state,
      postalCode: addr.zipCode?.toString() || null,
      country: addr.country || formCountry,
    };
  }
  return {
    street1: '',
    street2: null,
    city: '',
    state: null,
    postalCode: '',
    country: formCountry,
  };
};

const buildACHDetails = (
  contact: Contact | SelfContact,
  form: FiatACHPaymentForm,
  customer?: Customer,
): Record<string, unknown> => {
  const contactType = getContactType(contact);

  const details: Record<string, unknown> = {
    accountNumber: form.accountNumber,
    routingNumber: form.routingNumber,
    accountType: 'Checking',
    type: contactType,
    bankName: form.bankName,
    bankCountry: form.country,
    recipientAddress: getRecipientAddress(contact, form.country, customer),
  };

  if (contactType === ContactType.company) {
    details['registrationNumber'] = null;
  }

  return details;
};

const buildWireDetails = (
  contact: Contact | SelfContact,
  form: FiatWirePaymentForm,
  customer?: Customer,
): Record<string, unknown> => {
  const contactType = getContactType(contact);
  const bankAddress = form.address;

  const details: Record<string, unknown> = {
    accountNumber: form.accountNumber,
    routingNumber: form.routingNumber,
    accountType: BankAccountType.checking,
    type: contactType,
    bankName: form.bankName,
    bankBic: form.routingNumber,
    recipientAddress: getRecipientAddress(contact, form.country, customer),
    bankAddress: {
      street1: bankAddress.street,
      street2: bankAddress.complement,
      city: bankAddress.city,
      state: bankAddress.state || null,
      postalCode: bankAddress.zipCode,
      country: form.country,
    },
  };

  if (contactType === ContactType.company) {
    details['registrationNumber'] = null;
  }

  return details;
};

const buildSwiftDetails = (
  contact: Contact | SelfContact,
  form: FiatSwiftPaymentForm,
  customer?: Customer,
): Record<string, unknown> => {
  const contactType = getContactType(contact);
  const bankAddress = form.address;

  const isIban = isProbablyValidIBAN(form.iban);

  const details: Record<string, unknown> = {
    swiftCode: form.swiftCode,
    bankName: form.bankName,
    ...(isIban ? { iban: form.iban } : { accountNumber: form.iban }),
    accountType: BankAccountType.checking,
    type: contactType,
    isPobo: form.isPobo,
    recipientAddress: getRecipientAddress(contact, form.country, customer),
    bankAddress: {
      street1: bankAddress.street,
      street2: bankAddress.complement,
      city: bankAddress.city,
      state: bankAddress.state || null,
      postalCode: bankAddress.zipCode,
      country: form.country,
    },
  };

  if (contactType === ContactType.company) {
    details['registrationNumber'] = form.registrationNumber ?? null;
  }

  return details;
};

const buildPixDetails = (form: FiatPixPaymentForm): Record<string, unknown> => {
  return {
    pixKey: form.pixKey,
    documentNumber: form.documentNumber,
  };
};

const buildSpeiDetails = (form: FiatSpeiPaymentForm): Record<string, unknown> => {
  return {
    clabe: form.clabe,
    bankCode: form.bankCode,
    receiverName: form.receiverName,
  };
};

const buildCOPDetails = (form: FiatCOPPaymentForm): Record<string, unknown> => {
  return match<FiatCOPPaymentForm, Record<string, unknown>>(form)
    .when(
      isFiatCOPThirdPartyPaymentForm,
      (thirdPartyForm: FiatCOPThirdPartyCompanyPaymentForm): Record<string, unknown> => ({
        firstName: thirdPartyForm.firstName,
        accountType: thirdPartyForm.bankType,
        bankAccount: thirdPartyForm.bankAccount,
        bankCode: thirdPartyForm.bankCode,
        thirdPartyWithdrawal: thirdPartyForm.thirdPartyWithdrawal,
        documentId: thirdPartyForm.documentId,
        documentType: thirdPartyForm.documentType,
        email: thirdPartyForm.email,
      }),
    )
    .when(
      isFiatCOPBeneficiaryPaymentForm,
      (beneficiaryForm: FiatCOPBeneficiaryCompanyPaymentForm): Record<string, unknown> => ({
        firstName: beneficiaryForm.firstName,
        accountType: beneficiaryForm.bankType,
        bankAccount: beneficiaryForm.bankAccount,
        bankCode: beneficiaryForm.bankCode,
        thirdPartyWithdrawal: false,
        documentId: null,
        documentType: null,
        email: null,
      }),
    )
    .exhaustive();
};

const buildCVUDetails = (form: FiatCvuPaymentForm): Record<string, unknown> => {
  return {
    recipientName: form.recipientName,
    cvu: form.cvu,
  };
};

const getCurrencyForRail = (form: FiatPaymentForm): CurrencyCodeType => {
  return match<FiatPaymentForm, CurrencyCodeType>(form)
    .when(isFiatACHPaymentForm, (): CurrencyCodeType => CurrencyCodeType.USD)
    .when(isFiatWirePaymentForm, (): CurrencyCodeType => CurrencyCodeType.USD)
    .when(isFiatSwiftPaymentForm, (): CurrencyCodeType => CurrencyCodeType.USD)
    .when(isFiatPixPaymentForm, (): CurrencyCodeType => CurrencyCodeType.BRL)
    .when(isFiatSpeiPaymentForm, (): CurrencyCodeType => CurrencyCodeType.MXN)
    .when(isFiatCOPPaymentForm, (): CurrencyCodeType => CurrencyCodeType.COP)
    .when(isFiatCvuPaymentForm, (): CurrencyCodeType => CurrencyCodeType.ARS)
    .otherwise((): CurrencyCodeType => CurrencyCodeType.USD);
};

const getPaymentRailType = (form: FiatPaymentForm): PaymentRailType => {
  return match<FiatPaymentForm, PaymentRailType>(form)
    .when(isFiatACHPaymentForm, (): PaymentRailType => PaymentRailType.ACH)
    .when(isFiatWirePaymentForm, (): PaymentRailType => PaymentRailType.Wire)
    .when(isFiatSwiftPaymentForm, (): PaymentRailType => PaymentRailType.Swift)
    .when(isFiatPixPaymentForm, (): PaymentRailType => PaymentRailType.PIX)
    .when(isFiatSpeiPaymentForm, (): PaymentRailType => PaymentRailType.SPEI)
    .when(isFiatCOPPaymentForm, (): PaymentRailType => PaymentRailType.COP)
    .when(isFiatCvuPaymentForm, (): PaymentRailType => PaymentRailType.CVU)
    .otherwise((): PaymentRailType => PaymentRailType.unset);
};

const buildDetails = (
  contact: Contact | SelfContact,
  form: FiatPaymentForm,
  customer?: Customer,
): Record<string, unknown> => {
  return match<FiatPaymentForm, Record<string, unknown>>(form)
    .when(
      isFiatACHPaymentForm,
      (achForm: FiatACHPaymentForm): Record<string, unknown> =>
        buildACHDetails(contact, achForm, customer),
    )
    .when(
      isFiatWirePaymentForm,
      (wireForm: FiatWirePaymentForm): Record<string, unknown> =>
        buildWireDetails(contact, wireForm, customer),
    )
    .when(
      isFiatSwiftPaymentForm,
      (swiftForm: FiatSwiftPaymentForm): Record<string, unknown> =>
        buildSwiftDetails(contact, swiftForm, customer),
    )
    .when(
      isFiatPixPaymentForm,
      (pixForm: FiatPixPaymentForm): Record<string, unknown> => buildPixDetails(pixForm),
    )
    .when(
      isFiatSpeiPaymentForm,
      (speiForm: FiatSpeiPaymentForm): Record<string, unknown> => buildSpeiDetails(speiForm),
    )
    .when(
      isFiatCOPPaymentForm,
      (copForm: FiatCOPPaymentForm): Record<string, unknown> => buildCOPDetails(copForm),
    )
    .when(
      isFiatCvuPaymentForm,
      (cvuForm: FiatCvuPaymentForm): Record<string, unknown> => buildCVUDetails(cvuForm),
    )
    .otherwise((): Record<string, unknown> => ({}));
};

export const buildCreateRecipientRequest = (
  formData: FiatPaymentForm,
  beneficiaryId: string,
  contact: Contact | SelfContact,
  nickname?: string | null,
  contactId?: string,
  customer?: Customer,
): CreateRecipientRequest => {
  return {
    beneficiaryId: beneficiaryId,
    individualName: getIndividualName(contact),
    businessName: getBusinessName(contact),
    currency: getCurrencyForRail(formData),
    type: getPaymentRailType(formData),
    contactId: contactId ?? contact.id ?? null,
    belongsToBeneficiary: contact.id === null,
    nickname: nickname?.trim() || null,
    countryOfIncorporation: getCountryOfIncorporation(contact, customer),
    details: buildDetails(contact, formData, customer),
  };
};
